import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

export interface Dato {
  l: string;
  v: ReactNode;
  tono?: "navy" | "emerald" | "chart3" | "destructive" | "muted";
}

const tonoClase = (t?: Dato["tono"]) =>
  t === "emerald"
    ? "text-emerald"
    : t === "chart3"
      ? "text-chart-3"
      : t === "destructive"
        ? "text-destructive"
        : t === "muted"
          ? "text-muted-foreground"
          : "text-navy";

export function FilaDato({ dato }: { dato: Dato }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-dashed py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{dato.l}</span>
      <span className={`num text-sm font-medium ${tonoClase(dato.tono)}`}>{dato.v}</span>
    </div>
  );
}

interface TarjetaProps {
  titulo: ReactNode;
  fecha?: ReactNode;
  destacado: { l: string; v: ReactNode };
  resumen: Dato[];
  detalle?: Dato[];
  extra?: ReactNode;
}

/** Tarjeta por período: reemplaza a la fila de tabla en móvil. */
export function TarjetaPeriodo({
  titulo,
  fecha,
  destacado,
  resumen,
  detalle,
  extra,
}: TarjetaProps) {
  const [abierto, setAbierto] = useState(false);
  return (
    <div className="rounded-lg border border-navy/15 bg-card p-4 shadow-sm">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-navy">{titulo}</div>
          {fecha && <div className="num text-xs text-muted-foreground">{fecha}</div>}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{destacado.l}</p>
          <p className="num text-base font-semibold text-navy">{destacado.v}</p>
        </div>
      </div>
      <div className="mt-3">
        {resumen.map((d) => (
          <FilaDato key={d.l} dato={d} />
        ))}
      </div>
      {extra}
      {detalle && detalle.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setAbierto((v) => !v)}
            aria-expanded={abierto}
            className="mt-2 flex w-full items-center justify-between rounded-md bg-surface px-3 py-2.5 text-xs font-medium text-navy"
          >
            {abierto ? "Ocultar desglose" : "Ver desglose completo"}
            <ChevronDown
              className={`h-4 w-4 transition-transform ${abierto ? "rotate-180" : ""}`}
            />
          </button>
          {abierto && (
            <div className="mt-2">
              {detalle.map((d) => (
                <FilaDato key={d.l} dato={d} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Card resumen de la fila TOTAL, siempre visible al final del listado móvil. */
export function TarjetaTotal({ titulo = "TOTAL", datos }: { titulo?: string; datos: Dato[] }) {
  return (
    <div className="rounded-lg border border-navy/30 bg-navy-soft p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-navy">{titulo}</p>
      <div className="mt-2">
        {datos.map((d) => (
          <FilaDato key={d.l} dato={d} />
        ))}
      </div>
    </div>
  );
}
